import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Folder, File as FileIcon, ChevronRight, ChevronDown, Check, FolderPlus } from "lucide-react";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import * as cadviewer from "cadviewer";
import EventSystem from "../../events/EventSystem";
import TreeView, { ITreeViewOnExpandProps } from "../TreeView";
import { flattenTree } from "../TreeView/utils";
import {
  bmpIcon,
  csvIcon,
  dgnIcon,
  dwfIcon,
  dwgIcon,
  dxfIcon,
  gifIcon,
  jpgIcon,
  jsonIcon,
  pcfIcon,
  pdfIcon,
  pngIcon,
  svgIcon,
  tiffIcon,
  tifIcon,
  unkownIcon,
} from "../FolderListRightPanel/icons";

const FolderIcon = ({ isOpen, own }: { isOpen: boolean; own: boolean }) =>
  isOpen ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1 flex-shrink-0"
    >
      <path d="M19.906 9c.382 0 .749.057 1.094.162V9a3 3 0 0 0-3-3h-3.879a.75.75 0 0 1-.53-.22L11.47 3.66A2.25 2.25 0 0 0 9.879 3H6a3 3 0 0 0-3 3v3.162A3.756 3.756 0 0 1 4.094 9h15.812ZM4.094 10.5a2.25 2.25 0 0 0-2.227 2.568l.857 6A2.25 2.25 0 0 0 4.951 21H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-2.227-2.568H4.094Z" />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill={own ? "#FCDC12" : "#e8a87c"}
      className="size-6 mr-1 flex-shrink-0"
    >
      <path d="M19.5 21a3 3 0 0 0 3-3v-4.5a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h15ZM1.5 10.146V6a3 3 0 0 1 3-3h5.379a2.25 2.25 0 0 1 1.59.659l2.122 2.121c.14.141.331.22.53.22H19.5a3 3 0 0 1 3 3v1.146A4.483 4.483 0 0 0 19.5 9h-15a4.483 4.483 0 0 0-3 1.146Z" />
    </svg>
  );

const FileIconSvg = ({ filename }: { filename: string }) => {
  if (filename.toLocaleLowerCase().endsWith(".json")) return jsonIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwg")) return dwgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dxf")) return dxfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dwf")) return dwfIcon;
  else if (filename.toLocaleLowerCase().endsWith(".dgn")) return dgnIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tiff")) return tiffIcon;
  else if (filename.toLocaleLowerCase().endsWith(".tif")) return tifIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pdf")) return pdfIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".svg") ||
    filename.toLocaleLowerCase().endsWith(".svgz")
  )
    return svgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".gif")) return gifIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".jpg") ||
    filename.toLocaleLowerCase().endsWith(".jpeg")
  )
    return jpgIcon;
  else if (filename.toLocaleLowerCase().endsWith(".png")) return pngIcon;
  else if (filename.toLocaleLowerCase().endsWith(".bmp")) return bmpIcon;
  else if (
    filename.toLocaleLowerCase().endsWith(".xls") ||
    filename.toLocaleLowerCase().endsWith(".xlsx") ||
    filename.toLocaleLowerCase().endsWith(".csv")
  )
    return csvIcon;
  else if (filename.toLocaleLowerCase().endsWith(".pcf")) return pcfIcon;
  else return unkownIcon;
};

interface SaveAsSvgModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderStructure: any[];
}

export const SaveAsSvgModal: React.FC<SaveAsSvgModalProps> = ({
  isOpen,
  onClose,
  folderStructure,
}) => {
  const { config } = useConfig();
  const { ServerBackEndUrl } = config;
  const { user, currentDrawingPath, setCurrentDrawingPath } = React.useContext(CadviewerContext);

  const [filename, setFilename] = useState("");
  const [versionNote, setVersionNote] = useState("");

  const getCleanPath = (rawStr: string) => {
    let path = rawStr ? rawStr.replace(/\\/g, "/") : "";
    const contentIndex = path.indexOf("/content/");
    if (contentIndex !== -1) {
      path = path.substring(contentIndex + "/content/".length);
    } else if (path.startsWith("content/")) {
      path = path.substring("content/".length);
    } else if (path.startsWith("../content/")) {
      path = path.substring("../content/".length);
    }
    
    if (path.startsWith("versions/")) {
      path = path.substring("versions/".length);
    }
    return path;
  };

  const [selectedPath, setSelectedPath] = useState<string>("");
  const [expandedIds, setExpandedIds] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMode, setSaveMode] = useState<"overwrite" | "new_version">("new_version");

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const rootPath = React.useMemo(() => {
    if (folderStructure && folderStructure.length > 0) {
      const firstItemPath = folderStructure[0].path;
      if (firstItemPath) {
        const cleanItemPath = getCleanPath(firstItemPath);
        const parts = cleanItemPath.split('/');
        parts.pop(); // Remove the item's own name
        return parts.join('/');
      }
    }
    return "";
  }, [folderStructure]);

  const absoluteRootPath = React.useMemo(() => {
    if (folderStructure && folderStructure.length > 0) {
      const firstItemPath = folderStructure[0].path || "";
      const isWindows = firstItemPath.includes('\\');
      const separator = isWindows ? '\\' : '/';
      const parts = firstItemPath.split(separator);
      parts.pop();
      return parts.join(separator);
    }
    return "";
  }, [folderStructure]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setIsCreating(true);
    try {
      const parentAbsolute = selectedPath || absoluteRootPath;
      const separator = parentAbsolute.includes('\\') ? '\\' : '/';
      const newAbsoluteSelectedPath = parentAbsolute ? `${parentAbsolute}${separator}${newFolderName.trim()}` : newFolderName.trim();
      
      const cleanPath = selectedPath ? getCleanPath(selectedPath) : rootPath;
      const headers = new Headers();
      headers.append("Content-Type", "application/json");
      if (user?.token) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      const response = await fetch(
        ServerBackEndUrl + (ServerBackEndUrl.endsWith("/") ? "" : "/") + "settings/folders/create",
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            path: cleanPath,
            folderName: newFolderName.trim()
          })
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to create folder");
      }
      
      const newSelectedPath = cleanPath ? `${cleanPath}/${newFolderName.trim()}` : newFolderName.trim();
      setSelectedPath(newAbsoluteSelectedPath);
      
      // Auto-expand the parent folder so the user sees the new folder
      if (cleanPath) {
        // We use find on data, but data's element.metadata.path is absolute
        const parentNode = data.find((n: any) => n.metadata?.path === parentAbsolute);
        if (parentNode && !expandedIds.includes(parentNode.id)) {
          setExpandedIds([...expandedIds, parentNode.id]);
        }
      }
      
      setNewFolderName("");
      setIsCreatingFolder(false);
      EventSystem.publish("panel.folder_list.refresh", {});
    } catch (err: any) {
      setError(err.message || "Error creating folder");
    } finally {
      setIsCreating(false);
    }
  };

  const folder = React.useMemo(
    () => ({
      name: "",
      children: (folderStructure || []).map((f) => ({
        own: f.own,
        name: f.name,
        path: f.path,
        modified: f.modified,
        isFolder: f.isFolder,
        children: f.children,
      })),
    }),
    [folderStructure]
  );

  const data = React.useMemo(() => flattenTree(folder), [folder]);

  // Initialize from current drawing path on open, clean up on close
  useEffect(() => {
    if (isOpen) {
      if (currentDrawingPath) {
        let decodedPath = currentDrawingPath;
        try {
          decodedPath = decodeURIComponent(currentDrawingPath);
        } catch(e) {}

        const normalizedPath = getCleanPath(decodedPath);
        const parts = normalizedPath.split("/");
        
        if (parts.length > 0) {
          const fileWithExt = parts.pop() || "";
          
          let extractedFilename = fileWithExt;
          if (extractedFilename.toLowerCase().endsWith(".svg")) {
            extractedFilename = extractedFilename.substring(0, extractedFilename.length - 4);
          }
          setFilename(extractedFilename);
          
          const dirPath = parts.join("/");
          if (dirPath) {
            const normPath = (p: string) => p.replace(/\\/g, "/");
            const targetFolder = data.find(
              (node) =>
                typeof node.metadata?.path === "string" &&
                normPath(node.metadata.path).endsWith(dirPath)
            );

            if (targetFolder && targetFolder.id !== 0) {
              const ancestors: any[] = [];
              let currentId: any = targetFolder.id;

              while (currentId !== null && currentId !== undefined) {
                const parentNode = data.find((node) => node.id === currentId);
                const parent = parentNode?.parent;

                if (parent !== null && parent !== undefined) {
                  ancestors.push(parent);
                  currentId = parent;
                } else {
                  break;
                }
              }
              
              setExpandedIds(Array.from(new Set([targetFolder.id, ...ancestors])));
              setSelectedPath(targetFolder.metadata?.path as string);
            } else {
              // Fallback
              setSelectedPath(dirPath);
              setExpandedIds([]);
            }
          }
          
          // Strip any version stamp from the filename (e.g., .1234567890123.Version Note.svg)
          // so that saving a loaded version uses the original base filename.
          const versionMatch = fileWithExt.match(/(\.\d{13}\..*\.[^.]+)$/);
          let cleanFileWithExt = fileWithExt;
          if (versionMatch) {
            cleanFileWithExt = fileWithExt.substring(0, fileWithExt.length - versionMatch[0].length);
          }

          const lastDot = cleanFileWithExt.lastIndexOf(".");
          if (lastDot > 0) {
            setFilename(cleanFileWithExt.substring(0, lastDot));
          } else {
            setFilename(cleanFileWithExt);
          }
        }
      }
    } else {
      setFilename("");
      setVersionNote("");
      setSelectedPath("");
      setError("");
    }
  }, [isOpen, currentDrawingPath, folderStructure]);



  const handleSave = async () => {
    if (!filename.trim()) {
      setError("Please enter a filename");
      return;
    }
    if (!selectedPath) {
      setError("Please select a folder");
      return;
    }

    let finalFilename = filename.trim();
    if (!finalFilename.toLowerCase().endsWith(".svg")) {
      finalFilename += ".svg";
    }

    setIsSaving(true);
    setError("");

    try {
      // 1. Extract SVG from Canvas
      // CADViewer's current canvas is typically "floorPlan"
      const svgString = cadviewer.cvjs_extractSVGfromCanvas("floorPlan");
      if (!svgString) {
        throw new Error("Could not extract SVG from canvas. Is a drawing loaded?");
      }

      // 2. Create File object
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const file = new File([blob], finalFilename, { type: "image/svg+xml" });

      // 3. Prepare FormData
      const formdata = new FormData();
      formdata.append("files", file, finalFilename);
      formdata.append("global", "true");
      
      const cleanPath = getCleanPath(selectedPath);
      formdata.append("path", cleanPath);

      if (saveMode === "new_version") {
        formdata.append("targetFilename", finalFilename);
      }

      if (versionNote.trim()) {
        const versionData = {
          [finalFilename]: versionNote.trim(),
        };
        formdata.append("versionData", JSON.stringify(versionData));
      } else if (saveMode === "new_version") {
        const versionData = {
          [finalFilename]: "New version saved",
        };
        formdata.append("versionData", JSON.stringify(versionData));
      }

      const headers = new Headers();
      if (user?.token) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }

      const uploadEndpoint = "settings/folders/upload";
      const response = await fetch(
        ServerBackEndUrl + (ServerBackEndUrl.endsWith("/") ? "" : "/") + uploadEndpoint,
        {
          method: "POST",
          body: formdata,
          headers,
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error((data.error || data.message || "Upload failed") + ` (Debug: selectedPath="${selectedPath}", cleanPath="${cleanPath}")`);
      }

      // Success!
      const newPath = cleanPath ? `${cleanPath}/${finalFilename}` : finalFilename;
      if (setCurrentDrawingPath) {
        setCurrentDrawingPath(newPath);
      }

      // Trigger a refresh of the folder list if it's open
      EventSystem.publish("panel.folder_list.refresh", {});
      onClose();
    } catch (err: any) {
      console.error("Save SVG error:", err);
      setError(err.message || "Failed to save SVG");
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[9999999]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl">
                <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                      <FileIcon className="h-6 w-6 text-indigo-600" aria-hidden="true" />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left flex-1 min-w-0">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 truncate">
                        Save as SVG
                      </Dialog.Title>
                      <div className="mt-4 space-y-4">
                        
                        {/* Folder Browser */}
                        <div>
                          <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Select Destination Folder
                            </label>
                            <button 
                              onClick={() => setIsCreatingFolder(!isCreatingFolder)}
                              className="text-indigo-600 hover:text-indigo-800 flex items-center text-sm font-medium"
                              title="Create New Folder"
                            >
                              <FolderPlus size={16} className="mr-1" /> New Folder
                            </button>
                          </div>
                          
                          {isCreatingFolder && (
                            <div className="mb-2 flex items-center gap-2 bg-indigo-50 p-2 rounded-md border border-indigo-100">
                              <input 
                                type="text" 
                                className="block w-full rounded-md border-0 py-1 px-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600" 
                                placeholder="Folder name..."
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                autoFocus
                              />
                              <button 
                                onClick={handleCreateFolder}
                                disabled={!newFolderName.trim() || isCreating}
                                className="bg-indigo-600 text-white px-2 py-1 rounded text-sm disabled:opacity-50"
                              >
                                Create
                              </button>
                              <button 
                                onClick={() => { setIsCreatingFolder(false); setNewFolderName(""); }}
                                className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}

                          <div className="border border-gray-300 rounded-md h-48 overflow-y-auto overflow-x-hidden p-2 bg-gray-50 directory">
                            {data && data.length > 0 ? (
                              <TreeView
                                data={data}
                                aria-label="directory tree"
                                expandedIds={expandedIds}
                                onExpand={(node: ITreeViewOnExpandProps) => {
                                  if (node.isExpanded) {
                                    setExpandedIds([...expandedIds, node.element.id]);
                                  } else {
                                    setExpandedIds(
                                      expandedIds.filter((id) => id !== node.element.id)
                                    );
                                  }
                                }}
                                nodeRenderer={({
                                  element,
                                  isBranch,
                                  isExpanded,
                                  getNodeProps,
                                  level,
                                }) => {
                                  const nodePath = element.metadata?.path as string;
                                  const isSelected = isBranch && selectedPath === nodePath;
                                  
                                  const props = getNodeProps();
                                  const originalOnClick = props.onClick;

                                  return (
                                    <div
                                      {...props}
                                      onClick={(e) => {
                                        if (isBranch && nodePath !== undefined) {
                                          if (selectedPath === nodePath) {
                                            setSelectedPath("");
                                          } else {
                                            setSelectedPath(nodePath);
                                          }
                                        } else if (!isBranch && element.name) {
                                          let extractedFilename = element.name;
                                          if (extractedFilename.toLowerCase().endsWith(".svg")) {
                                            extractedFilename = extractedFilename.substring(0, extractedFilename.length - 4);
                                          }
                                          setFilename(extractedFilename);
                                        }
                                        if (originalOnClick) originalOnClick(e);
                                      }}
                                      className={`flex items-center text-sm cursor-pointer my-1 w-full rounded-md px-2 py-1 ${
                                        isSelected ? "bg-indigo-50 text-indigo-700 font-medium" : "text-gray-700 hover:bg-slate-100"
                                      }`}
                                      style={{ paddingLeft: 16 * (level - 1) + 8 }}
                                    >
                                      {isBranch ? (
                                        <FolderIcon isOpen={isExpanded} own={element.own === true} />
                                      ) : (
                                        <FileIconSvg filename={element.name || ""} />
                                      )}
                                      <span className="w-full truncate">{element.name}</span>
                                      {isSelected && <Check size={14} className="ml-auto flex-shrink-0 text-indigo-600" />}
                                    </div>
                                  );
                                }}
                              />
                            ) : (
                              <p className="text-sm text-gray-500 text-center mt-16">No folders available</p>
                            )}
                          </div>
                        </div>

                        {/* File Details */}
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              File Name
                            </label>
                            <div className="relative rounded-md shadow-sm">
                              <input
                                type="text"
                                className="block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                placeholder="my-drawing"
                                value={filename}
                                onChange={(e) => setFilename(e.target.value)}
                              />
                              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <span className="text-gray-500 sm:text-sm">.svg</span>
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Save Mode
                            </label>
                            <div className="flex items-center space-x-4 mt-2">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                  value="new_version"
                                  checked={saveMode === "new_version"}
                                  onChange={() => setSaveMode("new_version")}
                                />
                                <span className="ml-2 text-sm text-gray-700">New version</span>
                              </label>
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  className="text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                  value="overwrite"
                                  checked={saveMode === "overwrite"}
                                  onChange={() => setSaveMode("overwrite")}
                                />
                                <span className="ml-2 text-sm text-gray-700">Overwrite</span>
                              </label>
                            </div>
                          </div>
                        </div>

                        {saveMode === "new_version" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Version Note (Optional)
                            </label>
                            <input
                              type="text"
                              className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              placeholder="Initial SVG save"
                              value={versionNote}
                              onChange={(e) => setVersionNote(e.target.value)}
                            />
                          </div>
                        )}

                        {error && (
                          <p className="text-sm text-red-600 mt-2">{error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleSave}
                    disabled={isSaving || !selectedPath || !filename.trim()}
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
